<template>
  <TOverlay
    :show="bookingEstimatePending || deliveryCostPending"
   
    class="grow"
  >
    <ExperimentComponent experiment-key="rvz28661SimplifiedRtbFlow">
      <template #control>
        <slot name="header" />

        <EstimateDateRange
          ref="dateRangeComponent"
          :disabled-dates="disabledDates"
          :disabled="!rvIsPublished"
          :prevent-clear-dates="preventClearDates"
          flush
          @update="updateDates"
        />

        <EstimateGuests
          flush
          :disabled="!rvIsPublished"
          :hide-pets-switch="!rvAllowPets"
          @update="updateGuests"
        />

        <div class="grow my-4">
          <template v-if="bookingEstimateData && rvIsPublished">
            <LazyEstimateUpsellTipbox
              v-if="bookingEstimateData?.UpsellBooking"
              :country-code="financialCountry"
              :original-price="financialRenterTotal"
              :original-days="financialTotalNights"
              :upsell-price="bookingEstimateData.UpsellBooking?.Financial?.RenterTotal ?? 0"
              :upsell-days="bookingEstimateData.UpsellBooking?.Financial?.TotalNights ?? 0"
              class="my-3"
            />

            <TPriceBreakdown :price-breakdown="bookingEstimateData?.PriceBreakdownSummary" />

            <button
              type="button"
              class="mb-2 link font-semibold text-sm"
              @click="openPriceBreakdownModal()"
            >
              {{ t('priceSummary.link') }}
            </button>

            <TModalCritical
              v-model:visible="showPriceBreakdownModal"
              :title="t('priceSummary.modal.title')"
            >
              <TPriceBreakdown :price-breakdown="bookingEstimateData?.PriceBreakdown" />
            </TModalCritical>
          </template>

          <template v-else-if="!bookingEstimateData && !bookingEstimateError && rvIsPublished">
            <div class="text-sm text-center text-gray-500">
              {{ t('cannotBook') }}
            </div>

            <TTipbox
              v-if="canShowDiscountsOfferedTipbox"
              variant="highlight"
              :title="t('discountsOffered.title')"
              class="my-4"
            >
              <template #icon>
                <TIcon :icon="['far', 'tags']" />
              </template>
              {{ t('discountsOffered.message') }}
            </TTipbox>
          </template>

          <LazyRvRentalUnpublishedMessage
            v-if="!rvIsPublished"
            class="my-4"
          />

          <EstimateErrorTipbox />

          <EstimateDisclaimerInternationalPricing class="lg:hidden mt-2" />
        </div>
      </template>

      <template #variant>
        <div class="space-y-4 lg:space-y-5">
          <div class="space-y-2">
            <slot name="header" />

            <LazyRvRentalUnpublishedMessage v-if="!rvIsPublished" />
            <template v-else>
              <LazyEstimateUpsellTipbox
                v-if="bookingEstimateData?.UpsellBooking"
                :country-code="financialCountry"
                :original-price="financialRenterTotal"
                :original-days="financialTotalNights"
                :upsell-price="bookingEstimateData.UpsellBooking?.Financial?.RenterTotal ?? 0"
                :upsell-days="bookingEstimateData.UpsellBooking?.Financial?.TotalNights ?? 0"
              />

              <EstimateErrorTipbox />
            </template>
          </div>

          <div class="space-y-3">
            <EstimateDateRange
              ref="dateRangeComponent"
              :disabled-dates="disabledDates"
              :disabled="!rvIsPublished"
              :prevent-clear-dates="preventClearDates"
              flush
              @update="updateDates"
              @completed="triggerAutoAdvance"
            />

            <EstimateGuests
              flush
              :disabled="!rvIsPublished"
              :hide-pets-switch="!rvAllowPets"
              @update="updateGuests"
            />

            <EstimateDestination
              v-if="!rvIsStatic"
              ref="destinationComponent"
              :disabled="!rvIsPublished"
            />
          </div>

          <div v-if="bookingEstimateData && rvIsPublished">
            <TPriceBreakdown :price-breakdown="bookingEstimateData?.PriceBreakdownSummary" />

            <button
              type="button"
              class="link font-semibold text-sm"
              @click="openPriceBreakdownModal()"
            >
              {{ t('priceSummary.link') }}
            </button>

            <TModalCritical
              v-model:visible="showPriceBreakdownModal"
              :title="t('priceSummary.modal.title')"
            >
              <TPriceBreakdown :price-breakdown="bookingEstimateData?.PriceBreakdown" />
            </TModalCritical>

            <EstimateDisclaimerInternationalPricing class="lg:hidden mt-2" />
          </div>
        </div>
      </template>
    </ExperimentComponent>
  </TOverlay>
</template>

<script setup lang="ts">
import type { LocationQueryRaw } from '#vue-router'
import type { SearchDates } from '~/types/search'
import type { BookingEstimateGuests } from '~/types'

const props = withDefaults(defineProps<{
  pageSource?: string
  preventClearDates?: boolean
}>(), {
  pageSource: '',
})

const { t } = useI18n()

const {
  rvAllowPets,
  rvHasRangeDiscounts,
  rvOwnerId,
  rvId,
  rvRVSellingDate,
  rvBookingSeasonStart,
  rvBookingSeasonEnd,
  rvCalendars,
  rvIsPublished,
  rvIsStatic,
  rvComputedTimezone,
} = useRvDetails()

const disabledDates = computed(() => {
  return [
    ...getRvCalendarBlockedRangesFromSellingDate(rvRVSellingDate.value, rvComputedTimezone.value),
    ...getRvCalendarBlockedRangesFromBookingSeason(rvBookingSeasonStart.value, rvBookingSeasonEnd.value, rvComputedTimezone.value),
    ...getRvCalendarBlockedDates(rvCalendars.value, rvComputedTimezone.value),
  ]
})

const { userId } = useUser()

const userIsRvOwner = computed(() => {
  return rvOwnerId.value === userId.value
})

const {
  bookingEstimateError,
  bookingEstimateParams,
  bookingEstimatePending,
  bookingEstimateData,
  hasIncompleteDestination,
  financialCountry,
  financialRenterTotal,
  financialTotalNights,
  rvSnapshotChargePerHourOver,
  rvSnapshotSurchargePerExtraKM,
  rvSnapshotFreeHoursPerDay,
} = useBookingEstimate()

const { deliveryCostPending } = useDeliveryCost()

const { pageSource: pageSourceRoute } = usePageSource()

const showPriceBreakdownModal = ref(false)

function openPriceBreakdownModal() {
  showPriceBreakdownModal.value = true
}

function triggerTripQuoteEvents({ sendTripQuotePresented = false, sendTripQuoteViewed = false }: { sendTripQuotePresented?: boolean, sendTripQuoteViewed?: boolean }) {
  if (!sendTripQuotePresented && !sendTripQuoteViewed) {
    return
  }

  const payload = buildTripQuoteSegmentPayload({
    pageSource: props.pageSource || pageSourceRoute.value,
    rvId: rvId.value,
    financial: bookingEstimateData.value?.Financial || null,
    snapshot: {
      chargePerHourOver: rvSnapshotChargePerHourOver.value,
      surchargePerExtraKM: rvSnapshotSurchargePerExtraKM.value,
      freeHoursPerDay: rvSnapshotFreeHoursPerDay.value,
    },
    booking: {
      dateStart: bookingEstimateParams.value.dateStart,
      dateEnd: bookingEstimateParams.value.dateEnd,
      adults: bookingEstimateParams.value.adults,
      children: bookingEstimateParams.value.children,
      pets: bookingEstimateParams.value.pets,
      destination: bookingEstimateParams.value.destination?.name ?? '',
      rvUsage: bookingEstimateParams.value.rvUsage ?? '',
      deliveryDistance: bookingEstimateData.value?.DeliveryDistance ?? 0,
    },
  })

  if (sendTripQuotePresented) {
    trackTripQuotePresented(payload)
  }

  if (sendTripQuoteViewed) {
    trackTripQuoteViewed(payload)
  }
}

const { pause: pauseWatchExpandedBreakdown, resume: resumeWatchExpandedBreakdown } = watch(showPriceBreakdownModal, (value) => {
  if (!value || !bookingEstimateData.value || userIsRvOwner.value) {
    return
  }

  triggerTripQuoteEvents({ sendTripQuoteViewed: value })

  pauseWatchExpandedBreakdown()
})

const unwatch = watch(bookingEstimateData, () => {
  if (isMetaServer || !bookingEstimateData.value || userIsRvOwner.value) {
    return
  }

  triggerTripQuoteEvents({ sendTripQuotePresented: true, sendTripQuoteViewed: showPriceBreakdownModal.value })

  resumeWatchExpandedBreakdown()
}, {
  immediate: true,
})

onBeforeRouteLeave(() => {
  unwatch()
})

const dateRangeComponent = useTemplateRef('dateRangeComponent')
const destinationComponent = useTemplateRef('destinationComponent')

const route = useRoute()
async function updateRouteQuery(query: LocationQueryRaw) {
  await navigateTo({
    query: {
      ...route.query,
      ...query,
    },
  })
}

async function updateDates(newDates: SearchDates) {
  if (Boolean(newDates?.start && newDates?.end) || (!newDates?.start && !newDates?.end)) {
    await updateRouteQuery({
      StartDate: newDates?.start,
      EndDate: newDates?.end,
    })
  }
}

async function updateGuests(guests: BookingEstimateGuests) {
  await updateRouteQuery({
    Adults: guests.adults.toString(),
    Children: guests.children.toString(),
    PetFriendly: guests.pets ? null : undefined,
  })
}

const { getExperimentVariant } = useExperiment()
const isDisplayDiscountTypeBadgeExperimentVariant = computed(() => {
  return getExperimentVariant('rvz28041DisplayDiscountTypeBadge') === 'variant'
})

const canShowDiscountsOfferedTipbox = computed(() => {
  return rvHasRangeDiscounts.value && !isDisplayDiscountTypeBadgeExperimentVariant.value
})

function openCalendar() {
  // TODO: Check which type of error occurs instead of just opening the calendar.
  dateRangeComponent.value?.openCalendar()
}

function openDestination() {
  destinationComponent.value?.openDestinationPicker()
}

defineExpose({
  openCalendar,
  openDestination,
})

const isSimplifiedRtbFlowVariant = computed(() => {
  return getExperimentVariant('rvz28661SimplifiedRtbFlow') === 'variant'
})

function triggerAutoAdvance() {
  if (isSimplifiedRtbFlowVariant.value && hasIncompleteDestination.value) {
    openDestination()
  }
}
</script>

<i18n src="~~/i18n/locales/common/booking/request.json" lang="json" />

<i18n src="~~/i18n/locales/common/booking/price-summary.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "cannotBook": "Select your dates to get price details",
    "discountsOffered": {
      "title": "Discounts offered",
      "message": "Save when you book for a week or month"
    }
  },
  "fr": {
    "cannotBook": "Sélectionnez vos dates pour obtenir les détails du tarif",
    "discountsOffered": {
      "title": "Rabais offerts",
      "message": "Économisez lorsque vous réservez pour une semaine ou un mois"
    }
  }
}
</i18n>
